import type {TransitionDirection} from '@cohort/wallet/components/contexts/SpaceContext';
import {AboutPerkPanel} from '@cohort/wallet/components/space/AboutPerkPanel';
import {usePerkIntegrations} from '@cohort/wallet/hooks/usePerkIntegrations';
import useSpaceContext from '@cohort/wallet/hooks/useSpaceContext';
import SpacePage from '@cohort/wallet/layouts/space/SpacePage';
import {getMerchantSpaceRoute, getNftDetailRoute} from '@cohort/wallet/lib/Pages';
import type {AccessesAndOwnership} from '@cohort/wallet/lib/Utils';
import {getPerkIntancesAndOwnership} from '@cohort/wallet/lib/Utils';
import type {PerkWDto} from '@cohort/wallet-schemas/perk';
import type {PerkSpaceViewWDto} from '@cohort/wallet-schemas/perkAccess';
import React, {Fragment, useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';

interface PerkUsageProps {
  instanceAndOwnershipToUse: AccessesAndOwnership;
  returnTo: string;
}

const PerkUsage: React.FC<PerkUsageProps> = ({instanceAndOwnershipToUse, returnTo}) => {
  const {triggerRefresh, setTransitioning} = useSpaceContext();
  const navigate = useNavigate();

  const onPerkUsageSuccess = useCallback(
    async (transitionDirection?: TransitionDirection) => {
      await triggerRefresh();
      setTransitioning({active: true, enterFrom: transitionDirection ?? 'left'});
      navigate(returnTo, {replace: true});
    },
    [navigate, returnTo, setTransitioning, triggerRefresh]
  );

  const handleClose = useCallback(() => {
    setTransitioning({active: true, enterFrom: 'right'});
    navigate(returnTo, {
      replace: true,
    });
  }, [navigate, returnTo, setTransitioning]);

  const {getPerkIntegration} = usePerkIntegrations();
  const perkIntegrationId = instanceAndOwnershipToUse.access.perk.integration?.perkIntegrationId;
  if (perkIntegrationId === undefined) {
    return <Fragment />;
  }

  const perkIntegration = getPerkIntegration(perkIntegrationId);
  return (
    <perkIntegration.usageComponent
      access={instanceAndOwnershipToUse.access}
      ownership={instanceAndOwnershipToUse.ownership}
      onClose={handleClose}
      onPerkUsageSuccess={onPerkUsageSuccess}
    />
  );
};

interface PerkUsageNavigationState {
  referrer?: 'nft-detail-page';
}

const PerkUsagePage: React.FC = () => {
  const {space} = useSpaceContext();
  const navigate = useNavigate();
  const {perkId, ownershipId} = useParams() as {perkId: string; ownershipId: string};

  const [perk, setPerk] = useState<PerkWDto>();
  const [accessesAndOwnerships, setInstancesAndOwnerships] = useState<AccessesAndOwnership[]>([]);
  const [instanceAndOwnershipToUse, setInstanceAndOwnershipToUse] =
    useState<AccessesAndOwnership>();
  const location = useLocation();
  const navigationState = (location.state ?? {}) as PerkUsageNavigationState;

  const returnTo =
    navigationState.referrer === 'nft-detail-page'
      ? getNftDetailRoute(ownershipId)
      : getMerchantSpaceRoute();

  useEffect(() => {
    if (space === undefined) {
      return;
    }

    const perkAndInstances: PerkSpaceViewWDto | undefined = space.perks.find(
      p => p.perk.id === perkId
    );
    if (perkAndInstances === undefined) {
      navigate(returnTo);
      return;
    }
    const perk = perkAndInstances.perk;
    const perkAccessesAndOwnerships = getPerkIntancesAndOwnership(
      space,
      perkAndInstances.instances
    );
    const accessAndOwnership = perkAccessesAndOwnerships.find(p => p.ownership.id === ownershipId);

    if (accessAndOwnership === undefined || !accessAndOwnership.access.canBeUsed) {
      navigate(returnTo);
      return;
    }

    setPerk(perk);
    setInstancesAndOwnerships(perkAccessesAndOwnerships);
    setInstanceAndOwnershipToUse(accessAndOwnership);
  }, [ownershipId, navigate, navigationState.referrer, perkId, returnTo, space]);

  if (space === undefined || instanceAndOwnershipToUse === undefined || perk === undefined) {
    return <Fragment />;
  }

  const AboutPerkPanelComponent = (
    <AboutPerkPanel perk={perk} accessesAndOwnerships={accessesAndOwnerships} />
  );

  return (
    <SpacePage
      title={perk.displayName}
      tracking={{
        namespace: 'perks.usage',
        metadata: {
          appId: perk.integration?.perkIntegrationId,
          perkId: perk.id,
          digitalAssetId: instanceAndOwnershipToUse.ownership.digitalAsset.id,
          ownershipId: instanceAndOwnershipToUse.ownership.id,
          accessId: instanceAndOwnershipToUse.access.id,
        },
      }}
      leftPanel={AboutPerkPanelComponent}
    >
      <PerkUsage instanceAndOwnershipToUse={instanceAndOwnershipToUse} returnTo={returnTo} />
    </SpacePage>
  );
};

export default PerkUsagePage;
